// Desktop PTT hotkey — Ctrl+Shift+F hold-to-talk (§5 / §7.3).

import { useEffect, useRef } from "react";
import {
  isFireflyPttHotkeyChord,
  passesFireflyPttHotkeyGating,
} from "@/lib/firefly/fireflyPttGating";

export type UseFireflyPttHotkeyArgs = {
  enabled: boolean;
  onPttStart: () => void;
  onPttEnd: () => void;
};

export function useFireflyPttHotkey({
  enabled,
  onPttStart,
  onPttEnd,
}: UseFireflyPttHotkeyArgs): void {
  const enabledRef = useRef(enabled);
  const onPttStartRef = useRef(onPttStart);
  const onPttEndRef = useRef(onPttEnd);
  const hotkeyActiveRef = useRef(false);

  enabledRef.current = enabled;
  onPttStartRef.current = onPttStart;
  onPttEndRef.current = onPttEnd;

  useEffect(() => {
    if (!enabled && hotkeyActiveRef.current) {
      hotkeyActiveRef.current = false;
      onPttEndRef.current();
    }
  }, [enabled]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (!enabledRef.current || !isFireflyPttHotkeyChord(e)) return;
      if (!passesFireflyPttHotkeyGating(e.target)) return;
      if (hotkeyActiveRef.current) return;
      e.preventDefault();
      hotkeyActiveRef.current = true;
      onPttStartRef.current();
    };

    const onKeyUp = (e: KeyboardEvent) => {
      if (!hotkeyActiveRef.current || !isFireflyPttHotkeyChord(e)) return;
      e.preventDefault();
      hotkeyActiveRef.current = false;
      onPttEndRef.current();
    };

    window.addEventListener("keydown", onKeyDown, { capture: true });
    window.addEventListener("keyup", onKeyUp, { capture: true });
    return () => {
      window.removeEventListener("keydown", onKeyDown, { capture: true });
      window.removeEventListener("keyup", onKeyUp, { capture: true });
    };
  }, []);
}
